import {
  API,
  graphql,
  HubStatus,
  isAlertActive,
  SENSOR_OFFLINE_ALERT_TYPES,
  SensorAlert,
  SensorStatus,
  SensorType,
  ServiceRequestStatus,
} from "common";
import { useEffect, useState } from "react";

import { toDate } from "../utils/formatDate";

const LIST_SENSORS = /* GraphQL */ `
  query ListSensors($limit: Int, $nextToken: String) {
    listSensors(limit: $limit, nextToken: $nextToken) {
      id
      hubId
      type
      status
      lastReported
    }
  }
`;

// Only what the buckets need — common's listHubs also pulls every hub's
// config blob, which is kilobytes per row for nothing here.
const LIST_HUBS = /* GraphQL */ `
  query ListHubs($limit: Int, $nextToken: String) {
    listHubs(limit: $limit, nextToken: $nextToken) {
      id
      status
    }
  }
`;

const LIST_SERVICE_REQUESTS = /* GraphQL */ `
  query ListServiceRequests($limit: Int, $nextToken: String) {
    listServiceRequests(limit: $limit, nextToken: $nextToken) {
      items {
        id
        status
        createdAt
      }
      nextToken
    }
  }
`;

// listSensorAlerts scans the whole table per call, so keep the page big and the
// page count small. The cap is a runaway guard, not an expected code path.
const ALERT_PAGE_SIZE = 1000;
const ALERT_MAX_PAGES = 10;

/** Thresholds the process-health tiles deep-link with (`?age=7d`, `?age=14d`). */
export const STALE_ALERT_DAYS = 7;
export const OVERDUE_SERVICE_REQUEST_DAYS = 14;
/** Well past every offline-detector window (60 min sweep, 180 min for WQ). */
export const SILENT_SENSOR_HOURS = 24;

const CLOSED_SERVICE_REQUEST_STATUSES: string[] = [
  ServiceRequestStatus.CLOSED,
  ServiceRequestStatus.CANCELED,
];

// SensorType.HVAC and SensorType.POWER are aliases whose values are
// 'HVAC_AIRHANDLER_MONITOR' and 'POWER_MONITOR', so those keys already cover
// the real fleet types. The condenser has no alias and needs its own entry —
// without it the biggest HVAC slice fell through to the grey default.
const SENSOR_TYPE_COLORS: Record<string, string> = {
  [SensorType.HVAC]: "#3B82F6",
  [SensorType.HVAC_CONDENSER_MONITOR]: "#6366F1",
  [SensorType.LEAK_DETECTOR]: "#10B981",
  [SensorType.POWER]: "#F59E0B",
  [SensorType.AIR_QUALITY]: "#EF4444",
  [SensorType.WATER_QUALITY]: "#F97316",
};
const DEFAULT_SENSOR_TYPE_COLOR = "#6B7280";

interface FleetSensor {
  id: string;
  hubId: string;
  type: string;
  status: string;
  /** Epoch seconds. */
  lastReported?: number | null;
}

interface FleetHub {
  id: string;
  status: string;
}

interface FleetServiceRequest {
  id: string;
  status: string;
  createdAt: string;
}

export interface FleetAnalytics {
  /** Base stations reporting (status ACTIVE). */
  onlineHubCount: number;
  /** Base stations that went dark (status INACTIVE). Pending / maintenance are not "offline". */
  offlineHubCount: number;
  /** Registered sensors whose base station still exists, any status. */
  totalSensors: number;
  /** Sensors on a base station that is not ACTIVE. Not judged — the base station is the fault. */
  sensorsBehindOfflineHubs: number;
  /** On an online base station, reporting, nothing wrong. */
  activeSensorCount: number;
  /** On an online base station, reporting, with at least one active alert. */
  alertingSensorCount: number;
  /**
   * On an online base station, with an active not-reporting alert
   * (SENSOR_INACTIVE / AQ_SENSOR_INACTIVE). Same source as the Alerts page,
   * so the tile and the filtered list agree.
   */
  erroringSensorCount: number;
  distribution: Array<{ type: SensorType; count: number; color: string }>;

  // --- process health ---
  /** Active alerts, any hub, raised more than STALE_ALERT_DAYS ago. */
  staleAlertCount: number;
  /**
   * Sensors on an online base station, status ACTIVE, silent for more than
   * SILENT_SENSOR_HOURS with NO not-reporting alert. Should always be 0 — a
   * non-zero value means the offline detector missed one.
   */
  silentUnalertedSensorCount: number;
  /** Service requests not closed/canceled, opened more than OVERDUE_SERVICE_REQUEST_DAYS ago. */
  overdueServiceRequestCount: number;
}

const fetchSensors = async (): Promise<FleetSensor[]> => {
  const result = (await API.graphql({
    query: LIST_SENSORS,
    variables: { limit: 1000 },
  })) as any;
  return result.data?.listSensors || [];
};

const fetchHubs = async (): Promise<FleetHub[]> => {
  const result = (await API.graphql({
    query: LIST_HUBS,
    variables: { limit: 1000 },
  })) as any;
  return result.data?.listHubs || [];
};

const fetchServiceRequests = async (): Promise<FleetServiceRequest[]> => {
  const result = (await API.graphql({
    query: LIST_SERVICE_REQUESTS,
    variables: { limit: 1000 },
  })) as any;
  return result.data?.listServiceRequests?.items || [];
};

/**
 * Every unacknowledged alert across the fleet. `isAcknowledged: false` does the
 * filtering server-side; `isAlertActive` is the shared client-side guard that
 * also drops cleared/resolved rows.
 */
const fetchActiveAlerts = async (): Promise<SensorAlert[]> => {
  const alerts: SensorAlert[] = [];
  let nextToken: string | undefined;
  let page = 0;

  do {
    const response = (await API.graphql({
      query: graphql.listSensorAlerts,
      variables: {
        limit: ALERT_PAGE_SIZE,
        nextToken,
        isAcknowledged: false,
      },
    })) as any;

    const connection = response.data?.listSensorAlerts;
    alerts.push(...((connection?.items || []) as SensorAlert[]));
    nextToken = connection?.nextToken || undefined;
    page += 1;
  } while (nextToken && page < ALERT_MAX_PAGES);

  return alerts.filter(isAlertActive);
};

/**
 * One derived object for the whole Analytics page, so the tiles, the health
 * bars and the donut cannot disagree.
 *
 * Two classes of row are deliberately kept out of the sensor buckets:
 * - orphans — sensor rows whose hub no longer exists (the Sensor table keeps
 *   registrations for deleted hubs). They are nobody's fleet; dropped entirely.
 * - sensors behind a non-ACTIVE base station — a dead hub used to show up as
 *   8 "sensors in error", which buries the one real sensor failure. Those hubs
 *   are counted once, as offline base stations, and their sensors are reported
 *   as "not counted".
 */
export const deriveFleetAnalytics = (
  sensors: FleetSensor[],
  hubs: FleetHub[],
  alerts: SensorAlert[],
  serviceRequests: FleetServiceRequest[],
  now: number = Date.now(),
): FleetAnalytics => {
  const hubIds = new Set(hubs.map((h) => h.id));
  const onlineHubIds = new Set(
    hubs.filter((h) => h.status === HubStatus.Active).map((h) => h.id),
  );
  const known = sensors.filter((s) => hubIds.has(s.hubId));
  const reachable = known.filter((s) => onlineHubIds.has(s.hubId));
  const reachableIds = new Set(reachable.map((s) => s.id));

  const notReportingIds = new Set<string>();
  const alertingIds = new Set<string>();
  for (const alert of alerts) {
    const id = alert.sensorId;
    if (!id || !reachableIds.has(id)) continue;
    if ((SENSOR_OFFLINE_ALERT_TYPES as string[]).includes(alert.alertType)) {
      notReportingIds.add(id);
    } else {
      alertingIds.add(id);
    }
  }
  // Buckets stay disjoint: a sensor that is both silent and alerting on
  // something else is silent first.
  for (const id of notReportingIds) alertingIds.delete(id);

  const activeSensorCount = reachable.filter(
    (s) =>
      s.status === SensorStatus.Active &&
      !notReportingIds.has(s.id) &&
      !alertingIds.has(s.id),
  ).length;

  const byType = new Map<string, number>();
  for (const s of known) {
    const type = s.type || "UNKNOWN";
    byType.set(type, (byType.get(type) ?? 0) + 1);
  }
  const distribution = [...byType]
    .map(([type, count]) => ({
      type: type as SensorType,
      count,
      color: SENSOR_TYPE_COLORS[type] || DEFAULT_SENSOR_TYPE_COLOR,
    }))
    .sort((a, b) => b.count - a.count);

  const staleCutoffSec = now / 1000 - STALE_ALERT_DAYS * 86400;
  const staleAlertCount = alerts.filter(
    (a) => (a.timestamp ?? Infinity) < staleCutoffSec,
  ).length;

  const silentCutoffSec = now / 1000 - SILENT_SENSOR_HOURS * 3600;
  const silentUnalertedSensorCount = reachable.filter(
    (s) =>
      s.status === SensorStatus.Active &&
      typeof s.lastReported === "number" &&
      s.lastReported < silentCutoffSec &&
      !notReportingIds.has(s.id),
  ).length;

  const overdueCutoffMs = now - OVERDUE_SERVICE_REQUEST_DAYS * 86400 * 1000;
  const overdueServiceRequestCount = serviceRequests.filter((sr) => {
    if (CLOSED_SERVICE_REQUEST_STATUSES.includes(sr.status)) return false;
    const created = toDate(sr.createdAt);
    return !!created && created.getTime() < overdueCutoffMs;
  }).length;

  return {
    onlineHubCount: onlineHubIds.size,
    offlineHubCount: hubs.filter((h) => h.status === HubStatus.Inactive).length,
    totalSensors: known.length,
    sensorsBehindOfflineHubs: known.length - reachable.length,
    activeSensorCount,
    alertingSensorCount: alertingIds.size,
    erroringSensorCount: notReportingIds.size,
    distribution,
    staleAlertCount,
    silentUnalertedSensorCount,
    overdueServiceRequestCount,
  };
};

export const useFleetAnalytics = () => {
  const [data, setData] = useState<FleetAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sensors, hubs, alerts, serviceRequests] = await Promise.all([
          fetchSensors(),
          fetchHubs(),
          fetchActiveAlerts(),
          fetchServiceRequests(),
        ]);
        if (!cancelled) {
          setData(deriveFleetAnalytics(sensors, hubs, alerts, serviceRequests));
        }
      } catch (err) {
        console.error("Error fetching fleet analytics:", err);
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to fetch analytics",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
};
